import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { createSSEConnect } from '@/service/jSSEYs7'
import { limitArray, PRESET_CONFIGS } from '@/redux/utils/dataLimitHelper'
import { reconnectManager } from '@/utils/reconnectManager'
import { MONITORING_GROUP_UNPROCESSED_RECONNECT_ID } from '@/redux/alerts/monitoringGroupUnprocessedSlice'
import {
  getSeeData,
  getStreamKey,
  getStreamKeyFromMonitoringProcessedPayload
} from '@/views/commandCenter/profile/ServiceAlerts/monitoringSsePayloadUtils'
import { monitoringRecordOnDepartureSse } from './surveillanceMessage'

/**
 * 监控预警按摄像头（deviceId+channelNo）覆盖写：每路只留最新一条推送。
 * SSE 单条只描述一路，后到的有婴/安全包直接替换该路旧无婴，避免扁平列表里旧包回潮。
 * @returns {boolean} 是否已按 streamKey 写入 monitoring_alarm
 */
function upsertMonitoringAlarmByCamera (state, msg) {
  const sk = getStreamKey(msg)
  if (!sk || sk.indexOf('_unknown') === 0) return false
  if (!state.data.byType) state.data.byType = {}
  if (!Array.isArray(state.data.byType.monitoring_alarm)) {
    state.data.byType.monitoring_alarm = []
  }
  const prev = state.data.byType.monitoring_alarm
  state.data.byType.monitoring_alarm = [
    msg,
    ...prev.filter((item) => getStreamKey(item) !== sk)
  ].slice(0, PRESET_CONFIGS.SSE_MESSAGES.maxLength)
  if (state.data.monitoringAlarmProcessedStreamKeys) {
    delete state.data.monitoringAlarmProcessedStreamKeys[sk]
  }
  return true
}

function isDepartureSseEventType (type) {
  return type === 'departure_status'
    || type === 'departure_return'
    || type === 'departure_timeout_warning'
}

/** 离室 SSE 体：扁平 { deviceId, channelNo, status, departure }，兼容 { data: {...} } */
export function unwrapDepartureSsePayload (raw) {
  let cur = raw
  for (let depth = 0; depth < 4; depth += 1) {
    if (cur == null || typeof cur !== 'object') return null
    if (Array.isArray(cur)) {
      cur = cur.length > 0 ? cur[0] : null
      continue
    }
    if (cur.deviceId != null || cur.deviceSerial != null) return cur
    const inner = cur.data
    if (inner == null || inner === cur) return cur
    cur = inner
  }
  return (cur && typeof cur === 'object' && !Array.isArray(cur)) ? cur : null
}

function upsertDepartureSseByCamera (state, msg) {
  const payload = unwrapDepartureSsePayload(msg && msg.data)
  if (!payload) return
  const sk = getStreamKeyFromMonitoringProcessedPayload(payload)
  if (!sk) return
  if (!state.data.departureSseByStreamKey) state.data.departureSseByStreamKey = {}
  state.data.departureSseSeq = (state.data.departureSseSeq || 0) + 1
  state.data.departureSseByStreamKey[sk] = {
    eventType: msg.type,
    payload,
    seq: state.data.departureSseSeq
  }
}

// 在 reconnectManager 中的注册 ID
const SSE_RECONNECT_ID = 'java-sse'

/** 各页面/组件注册 SSE 时使用的稳定 consumerId（与 disconnect 成对） */
export const SSE_CONSUMER_IDS = {
  COMMAND_CENTER_PANEL: 'command-center-panel',
  SERVICE_ALERTS_LIST: 'service-alerts-list',
  YS_DEVICE_LIST: 'ys-device-list',
  SURVEILLANCE_LIST: 'surveillance-list',
  VISUAL_SCREEN: 'visual-screen'
}

/** 页面生命周期内 Java SSE 成功 onOpen 次数；用于区分「首连」与「断线/服务端重启后的重连」，以触发 HTTP 基线 refetch */
let javaSseSuccessfulOpenCount = 0

// ==================== 初始状态 ====================
const defaultState = {
  fetching: false,
  error: null,
  query: {},
  data: {
    connected: false,
    /** 本页未占 /sse/connect，靠其它页 BroadcastChannel 转发 */
    relayed: false,
    // 所有 SSE 消息（按时间顺序，用于统一展示/追踪）
    messages: [],
    // 按类型分类的消息缓存（用于快速筛选/按模块消费）
    byType: {
      system: [],
      error: [],
      connected: [],
      heartbeat: [],
      alarm: [],
      deviceStatus: [],
      notification: [],
      /**
       * 监控预警专用：服务端 SSE event 名 `monitoring_alarm`
       * 按摄像头（streamKey）覆盖写，数组内每路最多 1 条（最新推送）
       */
      monitoring_alarm: [],
      departure_status: [],
      departure_return: [],
      departure_timeout_warning: [],
      /** 服务端要求整页刷新（event 名 `pageReload`） */
      pageReload: [],
      message: []
    },
    /**
     * 监控预警已处理：`monitoring_alarm_processed` 推送后写入 streamKey → 时间戳；
     * 新 `monitoring_alarm` 同路会删除对应键（解除后再次告警可重新展示）。
     */
    monitoringAlarmProcessedStreamKeys: {},
    /** 离室 SSE 按摄像头覆盖：监控墙跟 eventType + payload 改格子，不再等 monitoring_alarm 猜回床 */
    departureSseByStreamKey: {},
    departureSseSeq: 0,
    // 统计信息：用于确认“是否真的入库/入了多少”（不影响展示）
    stats: {
      totalReceived: 0,
      lastReceivedAt: null,
      byType: {}
    }
  }
}

// SSE 连接对象不可序列化，不进入 Redux state
let eventSourceRef = null
/** 仍需要 SSE 的 consumerId 集合；按 id 成对注销，避免路由切换时误关新页连接 */
const sseConsumerIds = new Set()
// 自动重连控制（fetch-stream 版本不会自动重连，这里统一兜底）
let reconnectTimerRef = null
let reconnectAttempt = 0
let manuallyClosed = false

/** 一账号两页：只有持锁页真正 /sse/connect，其余页靠 BroadcastChannel 入库 */
const SSE_TAB_CHANNEL = 'aegis-java-sse'
const SSE_TAB_LOCK = 'aegis-java-sse'
const SSE_TAB_LEADER_LS = 'aegis-java-sse-leader'
const SSE_TAB_LEADER_STALE_MS = 8000
const SSE_TAB_ID = `t-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`

let sseTabChannel = null
let sseLockRelease = null
let isSseLeader = false
let followerLockAbort = null
let leaderHeartbeatTimer = null
let storageTakeoverTimer = null
let channelDispatch = null
let ingestRelay = false
let takeoverFromRelay = false
let lockMode = 'none'

const isConsumerSseAction = (arg) => arg?.isConsumer !== false

function postSseTab (msg) {
  if (!sseTabChannel || ingestRelay) return
  try {
    sseTabChannel.postMessage({ from: SSE_TAB_ID, ...msg })
  } catch (e) {
    // ignore
  }
}

function tryStorageLeader () {
  try {
    const raw = window.localStorage.getItem(SSE_TAB_LEADER_LS)
    if (raw) {
      const o = JSON.parse(raw)
      if (o && String(o.tabId) !== SSE_TAB_ID && Date.now() - Number(o.at) < SSE_TAB_LEADER_STALE_MS) {
        return false
      }
    }
    window.localStorage.setItem(
      SSE_TAB_LEADER_LS,
      JSON.stringify({ tabId: SSE_TAB_ID, at: Date.now() })
    )
    isSseLeader = true
    lockMode = 'storage'
    return true
  } catch (e) {
    isSseLeader = true
    lockMode = 'storage'
    return true
  }
}

function clearStorageLeaderIfSelf () {
  try {
    const raw = window.localStorage.getItem(SSE_TAB_LEADER_LS)
    const o = raw ? JSON.parse(raw) : null
    if (o && String(o.tabId) === SSE_TAB_ID) window.localStorage.removeItem(SSE_TAB_LEADER_LS)
  } catch (e) {
    // ignore
  }
}

function applyRelayMessage (msg) {
  const d = channelDispatch
  if (!d || !msg) return
  if (msg.kind === 'leader-heartbeat') {
    d(setSseConnected(true))
    d(setSseRelayed(true))
    return
  }
  if (msg.kind === 'leader-gone') {
    queueLeaderTakeover(d)
    return
  }
  if (msg.kind === 'sse-baseline-clear') {
    ingestRelay = true
    d(clearMonitoringAlarmBaseline())
    reconnectManager.refetchAfterJavaSseReconnect('跨页 SSE 基线清理')
    ingestRelay = false
    return
  }
  if (msg.kind !== 'sse-event') return
  ingestRelay = true
  d(setSseConnected(true))
  d(addSseMessage({
    id: Date.now() + Math.random(),
    type: msg.eventType,
    data: msg.data,
    time: new Date().toLocaleTimeString('zh-CN')
  }))
  if (msg.processed === true) d(addMonitoringAlarmProcessed(msg.data))
  ingestRelay = false
}

let pageHideBound = false

function bindSseTabPageHide () {
  if (pageHideBound || typeof window === 'undefined') return
  pageHideBound = true
  window.addEventListener('pagehide', () => {
    abortFollowerLockWait()
    if (!isSseLeader) return
    releaseSseLock()
    disposeEventSourceRef()
  })
}

function ensureSseTabChannel (dispatch) {
  channelDispatch = dispatch
  bindSseTabPageHide()
  if (typeof BroadcastChannel === 'undefined') return
  if (sseTabChannel) return
  sseTabChannel = new BroadcastChannel(SSE_TAB_CHANNEL)
  sseTabChannel.onmessage = (ev) => {
    const msg = ev.data
    if (!msg || msg.from === SSE_TAB_ID) return
    applyRelayMessage(msg)
  }
}

function broadcastSseEvent (eventType, data, processed) {
  if (!isSseLeader) return
  postSseTab({
    kind: 'sse-event',
    eventType,
    data,
    processed: processed === true
  })
}

function stopLeaderHeartbeat () {
  if (!leaderHeartbeatTimer) return
  clearInterval(leaderHeartbeatTimer)
  leaderHeartbeatTimer = null
}

function startLeaderHeartbeat () {
  stopLeaderHeartbeat()
  const beat = () => {
    postSseTab({ kind: 'leader-heartbeat' })
    if (lockMode === 'storage') tryStorageLeader()
  }
  beat()
  leaderHeartbeatTimer = setInterval(beat, 3000)
}

function releaseSseLock () {
  stopLeaderHeartbeat()
  if (isSseLeader) postSseTab({ kind: 'leader-gone' })
  isSseLeader = false
  takeoverFromRelay = false
  if (lockMode === 'storage') clearStorageLeaderIfSelf()
  lockMode = 'none'
  const rel = sseLockRelease
  sseLockRelease = null
  if (typeof rel === 'function') rel()
}

function abortFollowerLockWait () {
  if (followerLockAbort) {
    try { followerLockAbort.abort() } catch (e) {}
    followerLockAbort = null
  }
  if (storageTakeoverTimer) {
    clearInterval(storageTakeoverTimer)
    storageTakeoverTimer = null
  }
}

function acquireLockIfAvailable () {
  if (typeof navigator === 'undefined' || !navigator.locks || typeof navigator.locks.request !== 'function') {
    return Promise.resolve(tryStorageLeader())
  }
  return new Promise((resolve) => {
    let answered = false
    navigator.locks.request(SSE_TAB_LOCK, { ifAvailable: true }, (lock) => {
      if (!lock) {
        if (!answered) {
          answered = true
          resolve(false)
        }
        return undefined
      }
      lockMode = 'locks'
      isSseLeader = true
      if (!answered) {
        answered = true
        resolve(true)
      }
      return new Promise((release) => {
        sseLockRelease = release
      })
    })
  })
}

function queueLeaderTakeover (dispatch) {
  if (isSseLeader || sseConsumerIds.size === 0) return
  const hasLocks = typeof navigator !== 'undefined'
    && navigator.locks
    && typeof navigator.locks.request === 'function'
  if (!hasLocks) {
    if (storageTakeoverTimer) return
    storageTakeoverTimer = setInterval(() => {
      if (isSseLeader || sseConsumerIds.size === 0) {
        abortFollowerLockWait()
        return
      }
      if (!tryStorageLeader()) return
      abortFollowerLockWait()
      takeoverFromRelay = true
      dispatch(connectSse({ isConsumer: false, force: true }))
    }, 2000)
    return
  }
  if (followerLockAbort) return
  const ac = new AbortController()
  followerLockAbort = ac
  navigator.locks.request(SSE_TAB_LOCK, { signal: ac.signal }, () => {
    followerLockAbort = null
    if (sseConsumerIds.size === 0) return undefined
    lockMode = 'locks'
    isSseLeader = true
    takeoverFromRelay = true
    const held = new Promise((release) => {
      sseLockRelease = release
    })
    dispatch(connectSse({ isConsumer: false, force: true }))
    return held
  }).catch(() => {
    followerLockAbort = null
  })
}

const disposeEventSourceRef = () => {
  if (!eventSourceRef) return
  try { eventSourceRef.close() } catch (e) {}
  eventSourceRef = null
}

/** fetch-stream 与原生 EventSource 的 OPEN 均为 1 */
const isEventSourceOpen = (es) => es && es.readyState === 1

const registerSseConsumer = (consumerId) => {
  if (!consumerId || sseConsumerIds.has(consumerId)) return false
  sseConsumerIds.add(consumerId)
  manuallyClosed = false
  return true
}

const clearReconnectTimer = () => {
  if (reconnectTimerRef) {
    clearTimeout(reconnectTimerRef)
    reconnectTimerRef = null
  }
}

/**
 * 错误兜底：指数退避重连（1s → 30s），仅由 onError 触发
 * 网络恢复 / 页面可见 / 心跳超时 等"立即触发"场景统一交给 reconnectManager
 */
const scheduleReconnect = (dispatch, reason = '') => {
  if (manuallyClosed || !isSseLeader) return
  clearReconnectTimer()
  const delay = Math.min(30000, Math.pow(2, reconnectAttempt) * 1000)
  reconnectAttempt = Math.min(reconnectAttempt + 1, 10)
  reconnectTimerRef = setTimeout(() => {
    dispatch(addSseMessage({
      id: Date.now() + Math.random(),
      type: 'system',
      data: `准备重连（${Math.round(delay / 1000)}s）${reason ? `：${reason}` : ''}`,
      time: new Date().toLocaleTimeString('zh-CN')
    }))
    dispatch(connectSse({ isConsumer: false }))
  }, delay)
}

/**
 * 强制重连：关闭当前连接并立即发起新连接（绕过指数退避）
 * 提供给 reconnectManager 调用
 */
const forceReconnect = (dispatch, reason = '') => {
  if (manuallyClosed || !isSseLeader) return
  clearReconnectTimer()
  reconnectAttempt = 0
  disposeEventSourceRef()
  dispatch(setSseConnected(false))
  dispatch(addSseMessage({
    id: Date.now() + Math.random(),
    type: 'system',
    data: `强制重连${reason ? `：${reason}` : ''}`,
    time: new Date().toLocaleTimeString('zh-CN')
  }))
  dispatch(connectSse({ isConsumer: false }))
}

/**
 * 把当前 SSE 连接注册到通用重连管理器（只注册一次）
 * 后续由 reconnectManager 统一负责：
 *   - online 事件 → reconnectAll
 *   - visibilitychange→visible 且心跳超时 → reconnectOne
 *   - 心跳看门狗超时 → reconnectOne
 */
const ensureRegistered = (dispatch) => {
  reconnectManager.register({
    id: SSE_RECONNECT_ID,
    type: 'sse',
    // 心跳超时阈值：放宽到 180s，避免后端 30s 心跳偶发抖动时被 watchdog 抢先误判
    heartbeatTimeoutMs: 180 * 1000,
    shouldReconnect: () => !manuallyClosed && isSseLeader,
    reconnect: (reason) => forceReconnect(dispatch, reason || 'reconnectManager')
  })
}

/**
 * 建立 SSE 连接（thunk）
 * 说明：
 * - 连接对象保存在模块作用域，避免进入 redux state
 * - 事件回调里只 dispatch action，不做 UI 副作用
 */
export const connectSse = createAsyncThunk(
  'sse/connectSse',
  async (arg, { dispatch, getState, rejectWithValue }) => {
    const consumer = isConsumerSseAction(arg)
    const consumerId = consumer ? arg?.consumerId : null
    if (consumer) {
      if (!consumerId) {
        console.warn('[SSE] connectSse 缺少 consumerId，引用计数可能异常')
      } else if (!sseConsumerIds.has(consumerId)) {
        registerSseConsumer(consumerId)
      } else {
        manuallyClosed = false
      }
    }

    try {
      ensureSseTabChannel(dispatch)

      const javaToken = getState()?.app?.javaToken
      if (!javaToken) {
        if (!isSseLeader) {
          const gotLock = await acquireLockIfAvailable()
          if (!gotLock) {
            queueLeaderTakeover(dispatch)
            dispatch(setSseRelayed(true))
            dispatch(setSseConnected(true))
            return { relayed: true, waitingForToken: true }
          }
        }
        dispatch(setSseRelayed(false))
        startLeaderHeartbeat()
        scheduleReconnect(dispatch, '等待 javaToken')
        return { waitingForToken: true }
      }

      if (!isSseLeader) {
        const gotLock = await acquireLockIfAvailable()
        if (!gotLock) {
          queueLeaderTakeover(dispatch)
          dispatch(setSseRelayed(true))
          dispatch(setSseConnected(true))
          dispatch(setSseError(null))
          return { relayed: true }
        }
      }
      dispatch(setSseRelayed(false))
      startLeaderHeartbeat()

      const forceNewConnection = arg?.force === true
      if (forceNewConnection) {
        clearReconnectTimer()
        reconnectAttempt = 0
        if (eventSourceRef) {
          disposeEventSourceRef()
          dispatch(setSseConnected(false))
        }
      }

      if (!forceNewConnection && isEventSourceOpen(eventSourceRef)) {
        dispatch(setSseConnected(true))
        dispatch(addSseMessage({
          id: Date.now() + Math.random(),
          type: 'system',
          data: '已存在连接，无需重复连接',
          time: new Date().toLocaleTimeString('zh-CN')
        }))
        return { alreadyConnected: true }
      }

      if (eventSourceRef) {
        disposeEventSourceRef()
      }

      dispatch(addSseMessage({
        id: Date.now() + Math.random(),
        type: 'system',
        data: '开始建立连接...',
        time: new Date().toLocaleTimeString('zh-CN')
      }))
      dispatch(setSseConnected(false))
      dispatch(setSseError(null))
      manuallyClosed = false
      clearReconnectTimer()

      const addMessage = (type, data, opts) => {
        // 统一入库结构：
        // - data: 尽量是"可渲染的对象"（已 JSON.parse），如果是字符串则直接使用
        const time = new Date().toLocaleTimeString('zh-CN')
        dispatch(addSseMessage({
          id: Date.now() + Math.random(),
          type,
          data,
          time
        }))
        if (opts && opts.processed === true) {
          dispatch(addMonitoringAlarmProcessed(data))
        }
        if (type === 'heartbeat' || type === 'system' || type === 'pageReload') return
        broadcastSseEvent(type, data, opts && opts.processed === true)
      }

      const es = createSSEConnect({
        // 任何 SSE event 都视为"活跃"，集中刷新看门狗时间戳
        // 这样不会因为后端心跳 event 名变化（heartbeat / ping / keepalive）而漏 touch
        onActivity: () => {
          reconnectManager.touch(SSE_RECONNECT_ID)
          // 与 SSE 同源：监控列表靠 SSE 增量更新，HTTP group_unprocessed 仅基线；
          // 看门狗若只看 HTTP 成功时间会误在「长连正常」时 force 拉取。任一字节流（含 :heartbeat）视为整条链路仍活。
          reconnectManager.touch(MONITORING_GROUP_UNPROCESSED_RECONNECT_ID)
        },
        onOpen: () => {
          dispatch(setSseConnected(true))
          dispatch(setSseError(null))
          // 连接成功后重置重连计数
          reconnectAttempt = 0
          // 注册到通用重连管理器（统一管理 online / visibility / 心跳超时）
          ensureRegistered(dispatch)
          reconnectManager.touch(SSE_RECONNECT_ID)
          reconnectManager.touch(MONITORING_GROUP_UNPROCESSED_RECONNECT_ID)
          javaSseSuccessfulOpenCount += 1
          // 服务端重启/断连后 SSE 会重连成功，但 HTTP 基线不会自动拉；首连不刷，第二次及以后 onOpen：
          // 1）清掉断连期间过期的按路监控场景（否则旧有婴会挡住新 list 的无婴）
          // 2）再触发 list / group_unprocessed 等 HTTP 重拉
          // 从页接棒后的第一次 onOpen 也要刷，避免主页关掉后桶停在旧场景
          if (javaSseSuccessfulOpenCount > 1 || takeoverFromRelay) {
            dispatch(clearMonitoringAlarmBaseline())
            reconnectManager.refetchAfterJavaSseReconnect()
            postSseTab({ kind: 'sse-baseline-clear' })
            takeoverFromRelay = false
          }
          // addMessage('system', '连接已建立')
        },
        onError: (error) => {
          console.error('[SSE] 连接错误:', error)
          // addMessage('error', 'SSE 连接错误')
          dispatch(setSseError(error?.message || 'SSE 连接错误'))

          if (es.readyState === EventSource.CLOSED) {
            dispatch(setSseConnected(false))
            // 断线时释放引用，允许重新创建
            eventSourceRef = null
            // addMessage('system', '连接已关闭')
            scheduleReconnect(dispatch, '连接已关闭')
          } else if (es.readyState === EventSource.CONNECTING) {
            dispatch(setSseConnected(false))
            eventSourceRef = null
            scheduleReconnect(dispatch, 'connecting')
          }
        },
        onClose: () => {
          dispatch(setSseConnected(false))
          eventSourceRef = null
          addMessage('system', '连接已关闭')
        }
      })
      // 默认 message 事件：用 addEventListener 统一处理
      es.addEventListener('message', (event) => {
        dispatch(setSseConnected(true))
        reconnectManager.touch(SSE_RECONNECT_ID)
        const raw = event.data
        let data = raw
        try {
          data = JSON.parse(raw)
        } catch (e) {
          // ignore
        }
        addMessage('message', data)
      })
      // 监听 connected 事件
      es.addEventListener('connected', (event) => {
        dispatch(setSseConnected(true))
        reconnectManager.touch(SSE_RECONNECT_ID)
        const raw = event.data
        let data = raw
        try {
          data = JSON.parse(raw)
        } catch (e) {
          // ignore
        }
        addMessage('connected', data)
      })

      // 监听 heartbeat 事件
      es.addEventListener('heartbeat', (event) => {
        const raw = event.data
        // 收到任何 heartbeat 都刷新活跃时间，避免被看门狗误判
        reconnectManager.touch(SSE_RECONNECT_ID)
        addMessage('heartbeat', { ts: raw })
      })

      // 监听 alarm 事件
      es.addEventListener('alarm', (event) => {
        const raw = event.data
        try {
          const data = JSON.parse(raw)
          addMessage('alarm', data)
        } catch (e) {
          addMessage('alarm', raw)
        }
      })

      // 监听 deviceStatus 事件
      es.addEventListener('deviceStatus', (event) => {
        const raw = event.data
        try {
          const data = JSON.parse(raw)
          addMessage('deviceStatus', data)
        } catch (e) {
          addMessage('deviceStatus', raw)
        }
      })

      // 监听 notification 事件
      es.addEventListener('notification', (event) => {
        const raw = event.data
        try {
          const data = JSON.parse(raw)
          addMessage('notification', data)
        } catch (e) {
          addMessage('notification', raw)
        }
      })

      /**
       * 监控推送（告警 / 解除同一条线）：按摄像头覆盖成该路最新记录，监控墙直接跟着变。
       * 记录与 list.latestMonitoringMessage 同一扁平结构，场景字段在记录本身。
       */
      const pushMonitoringAlarm = (data) => {
        addMessage('monitoring_alarm', data)
      }
      const pushMonitoringAlarmProcessed = (data) => {
        addMessage('monitoring_alarm', data, { processed: true })
      }

      // 监控预警：event 名 monitoring_alarm，与通用 message 分桶隔离
      es.addEventListener('monitoring_alarm', (event) => {
        const raw = event.data
        try {
          const data = JSON.parse(raw)
          pushMonitoringAlarm(data)
        } catch (e) {
          addMessage('monitoring_alarm', raw)
        }
      })

      es.addEventListener('monitoring_alarm_processed', (event) => {
        const raw = event.data
        try {
          const data = JSON.parse(raw)
          // 先写场景再记 processed，保证「该路已处理」标记不被覆盖写清掉
          pushMonitoringAlarmProcessed(data)
        } catch (e) {
          console.error('[SSE] monitoring_alarm_processed 解析失败:', e)
        }
      })

      const listenMonitoringStatistics = (eventName) => {
        es.addEventListener(eventName, (event) => {
          const raw = event.data
          try {
            addMessage(eventName, JSON.parse(raw))
          } catch (e) {
            console.error(`[SSE] ${eventName} 解析失败:`, e)
          }
        })
      }
      listenMonitoringStatistics('monitoring_alam_staisies')
      listenMonitoringStatistics('monitoring_alarm_statistics')

      const listenDepartureJson = (eventName) => {
        es.addEventListener(eventName, (event) => {
          const raw = event.data
          try {
            addMessage(eventName, JSON.parse(raw))
          } catch (e) {
            addMessage(eventName, raw)
          }
        })
      }
      listenDepartureJson('departure_status')
      listenDepartureJson('departure_return')
      listenDepartureJson('departure_timeout_warning')

      // 服务端通知整页刷新；列表条件由各页自行落 sessionStorage（如监控墙店铺）
      es.addEventListener('pageReload', (event) => {
        const raw = event.data
        let data = raw
        try {
          data = JSON.parse(raw)
        } catch (e) {
          // ignore
        }
        addMessage('pageReload', data)
        try {
          const guardKey = 'sse.pageReload.at'
          const now = Date.now()
          const last = Number(window.sessionStorage.getItem(guardKey) || 0)
          // 短窗内只刷一次，避免重连后连环 reload
          if (now - last < 5000) return
          window.sessionStorage.setItem(guardKey, String(now))
        } catch (e) {
          // ignore
        }
        // reload 前记下监控墙是否整墙全屏；刷新后 List 从 sessionStorage 恢复
        try {
          const conditionsKey = 'surveillance.list.conditions'
          let prev = {}
          const rawConditions = window.sessionStorage.getItem(conditionsKey)
          if (rawConditions) {
            const parsed = JSON.parse(rawConditions)
            if (parsed && typeof parsed === 'object') prev = parsed
          }
          const wallFullscreen = Boolean(
            document.fullscreenElement
            || document.webkitFullscreenElement
            || document.msFullscreenElement
            || document.documentElement.classList.contains('surveillance-wall-fullscreen')
          )
          window.sessionStorage.setItem(
            conditionsKey,
            JSON.stringify({ ...prev, wallFullscreen })
          )
        } catch (e) {
          // ignore
        }
        window.location.reload()
      })
      // 注意：不要同时设置 es.onmessage，否则会导致 message 重复记录

      eventSourceRef = es
      return { connected: true }
    } catch (error) {
      console.error('[SSE] 创建连接失败:', error)
      if (isSseLeader) scheduleReconnect(dispatch, '创建连接失败')
      dispatch(setSseError(error?.message || '创建连接失败'))
      dispatch(setSseConnected(false))
      dispatch(addSseMessage({
        id: Date.now() + Math.random(),
        type: 'error',
        data: '创建连接失败: ' + (error?.message || '未知错误'),
        time: new Date().toLocaleTimeString('zh-CN')
      }))
      return rejectWithValue(error?.message || '创建连接失败')
    }
  }
)

/**
 * 关闭 SSE 连接（thunk）
 */
export const disconnectSse = createAsyncThunk(
  'sse/disconnectSse',
  async (arg, { dispatch }) => {
    const consumer = isConsumerSseAction(arg)
    const consumerId = consumer ? arg?.consumerId : null
    if (consumer) {
      if (!consumerId || !sseConsumerIds.has(consumerId)) {
        // 首页 Panel 尚未完成 connect 就跳转时，unmount 的 disconnect 不应关掉预警页新建连接
        return { notRegistered: true }
      }
      sseConsumerIds.delete(consumerId)
      if (sseConsumerIds.size > 0) {
        return { keptAlive: true, consumerCount: sseConsumerIds.size }
      }
    }

    manuallyClosed = true
    sseConsumerIds.clear()
    abortFollowerLockWait()
    clearReconnectTimer()
    reconnectManager.unregister(SSE_RECONNECT_ID)
    reconnectAttempt = 0
    javaSseSuccessfulOpenCount = 0
    releaseSseLock()
    dispatch(setSseRelayed(false))
    if (eventSourceRef) {
      disposeEventSourceRef()
      dispatch(setSseConnected(false))
      dispatch(addSseMessage({
        id: Date.now() + Math.random(),
        type: 'system',
        data: '连接已手动关闭',
        time: new Date().toLocaleTimeString('zh-CN')
      }))
    } else {
      dispatch(addSseMessage({
        id: Date.now() + Math.random(),
        type: 'system',
        data: '当前没有活动连接',
        time: new Date().toLocaleTimeString('zh-CN')
      }))
    }
  }
)

/**
 * SSE 消息存储（独立模块）
 * - 只做状态管理，不在 reducer 内执行副作用
 */
const sseSlice = createSlice({
  name: 'sse',
  initialState: defaultState,
  reducers: {
    resetSseState: () => defaultState,
    setSseConnected: (state, action) => {
      state.data.connected = Boolean(action.payload)
    },
    setSseRelayed: (state, action) => {
      state.data.relayed = Boolean(action.payload)
    },
    setSseError: (state, action) => {
      state.error = action.payload || null
    },
    clearSseMessages: (state) => {
      state.data.messages = []
      state.data.byType = {
        system: [],
        error: [],
        connected: [],
        heartbeat: [],
        alarm: [],
        deviceStatus: [],
        notification: [],
        monitoring_alarm: [],
        departure_status: [],
        departure_return: [],
        departure_timeout_warning: [],
        pageReload: [],
        message: []
      }
      state.data.monitoringAlarmProcessedStreamKeys = {}
      state.data.departureSseByStreamKey = {}
      state.data.departureSseSeq = 0
      state.data.stats = {
        totalReceived: 0,
        lastReceivedAt: null,
        byType: {}
      }
    },
    /**
     * SSE 断线重连后：丢掉断连期间过期的按路监控场景，交给 HTTP list / group_unprocessed 重新铺底；
     * 之后新推送再按摄像头覆盖。不清其它分桶（pageReload 等）。
     */
    clearMonitoringAlarmBaseline: (state) => {
      if (!state.data.byType) state.data.byType = {}
      state.data.byType.monitoring_alarm = []
      state.data.monitoringAlarmProcessedStreamKeys = {}
      state.data.departureSseByStreamKey = {}
    },
    addMonitoringAlarmProcessed: (state, action) => {
      const sk = getStreamKeyFromMonitoringProcessedPayload(action.payload)
      if (!sk) return
      if (!state.data.monitoringAlarmProcessedStreamKeys) {
        state.data.monitoringAlarmProcessedStreamKeys = {}
      }
      state.data.monitoringAlarmProcessedStreamKeys[sk] = Date.now()
    },
    addSseMessage: (state, action) => {
      const msg = action.payload || {}
      const type = msg.type || 'message'

      // 统计：无论是否落库（如 system 被屏蔽），都计数，方便排查
      if (!state.data.stats) {
        state.data.stats = { totalReceived: 0, lastReceivedAt: null, byType: {} }
      }
      state.data.stats.totalReceived = (state.data.stats.totalReceived || 0) + 1
      state.data.stats.lastReceivedAt = Date.now()
      state.data.stats.byType = state.data.stats.byType || {}
      state.data.stats.byType[type] = (state.data.stats.byType[type] || 0) + 1

      // system 类型信息不写入库（按需求先屏蔽）
      if (type === 'system') {
        return
      }

      // 1) 全量消息：最新在前（插到数组第一位），最多保留 300 条
      // 因为 limitArray 的 FIFO 策略是“从末尾截取”，不适用于“最新在前”的数组，
      // 所以这里直接 slice(0, maxLength) 保留前 N 条。
      state.data.messages = [msg, ...state.data.messages].slice(0, PRESET_CONFIGS.SSE_MESSAGES.maxLength)

      // 2) 分类型写入
      if (!state.data.byType) state.data.byType = {}

      // 监控预警：按摄像头覆盖；落错成通用 message 但带 messageBO 的也并入同桶
      if (type === 'monitoring_alarm') {
        if (!upsertMonitoringAlarmByCamera(state, msg)) {
          if (!Array.isArray(state.data.byType.monitoring_alarm)) {
            state.data.byType.monitoring_alarm = []
          }
          state.data.byType.monitoring_alarm = [
            msg,
            ...state.data.byType.monitoring_alarm
          ].slice(0, PRESET_CONFIGS.SSE_MESSAGES.maxLength)
        }
        return
      }

      if (!Array.isArray(state.data.byType[type])) state.data.byType[type] = []
      state.data.byType[type] = [msg, ...state.data.byType[type]].slice(0, PRESET_CONFIGS.SSE_MESSAGES.maxLength)

      if (type === 'message') {
        const see = getSeeData(msg)
        if (see && typeof see === 'object' && (
          see.hasBaby !== undefined || see.isViolation !== undefined
          || see.messageBO || see.messageBody || see.messageMap
        )) {
          upsertMonitoringAlarmByCamera(state, msg)
        }
      }

      if (isDepartureSseEventType(type)) {
        upsertDepartureSseByCamera(state, msg)
        if (type !== 'departure_timeout_warning') {
          const payload = unwrapDepartureSsePayload(msg && msg.data)
          const bed = String(payload && payload.status != null ? payload.status : '').trim().toLowerCase()
          if (type !== 'departure_matched' && bed !== 'departure_matched') {
            const record = monitoringRecordOnDepartureSse(payload)
            if (record) {
              upsertMonitoringAlarmByCamera(state, {
                id: msg.id,
                type: 'monitoring_alarm',
                data: record,
                time: msg.time
              })
            }
          }
        }
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(connectSse.pending, (state) => {
        state.fetching = true
        state.error = null
      })
      .addCase(connectSse.fulfilled, (state) => {
        state.fetching = false
      })
      .addCase(connectSse.rejected, (state, action) => {
        state.fetching = false
        state.error = action.payload || '创建连接失败'
      })
  }
})

export const {
  resetSseState,
  setSseConnected,
  setSseRelayed,
  setSseError,
  clearSseMessages,
  clearMonitoringAlarmBaseline,
  addSseMessage,
  addMonitoringAlarmProcessed
} = sseSlice.actions

export default sseSlice.reducer

/**
 * 监控相关 SSE 列表：以按摄像头覆盖后的 monitoring_alarm 为准。
 * message 桶里已带 messageBO 的会在入库时并进 monitoring_alarm，这里不再二次拼接，避免双份。
 * @param {Object} sseObj - state.sse
 * @returns {Array}
 */
export function getMonitoringSseMessageList(sseObj) {
  const byType = sseObj?.data?.byType
  if (!byType) return []
  const alarm = Array.isArray(byType.monitoring_alarm) ? byType.monitoring_alarm : []
  const rawMsg = Array.isArray(byType.message) ? byType.message : []
  if (rawMsg.length === 0) return alarm
  const extra = []
  for (let i = 0; i < rawMsg.length; i += 1) {
    const item = rawMsg[i]
    if (!item) continue
    const see = getSeeData(item)
    if (see && typeof see === 'object' && (
      see.hasBaby !== undefined || see.isViolation !== undefined
      || see.messageBO || see.messageBody || see.messageMap
    )) {
      continue
    }
    extra.push(item)
  }
  if (extra.length === 0) return alarm
  return alarm.concat(extra)
}

/**
 * 监控预警分桶：按摄像头覆盖后的列表（每路最多 1 条最新推送）。
 * 入库时已把带 messageBO 的兼容 `message` 并入，消费方不必再合并 message 桶。
 * @param {Object} sseObj - state.sse
 * @returns {Array}
 */
export function getMonitoringAlarmSseList(sseObj) {
  const t = sseObj?.data?.byType
  if (!t) return []
  const arr = t.monitoring_alarm
  if (!Array.isArray(arr)) return []
  return arr
}

/** 已 SSE 宣告处理的设备+通道 streamKey 映射（供监控预警列表过滤） */
export function getMonitoringAlarmProcessedStreamKeysMap(sseObj) {
  const m = sseObj?.data?.monitoringAlarmProcessedStreamKeys
  if (!m || typeof m !== 'object') return {}
  return m
}

const EMPTY_DEPARTURE_SSE_MAP = {}

/** 离室 SSE 按摄像头覆盖后的最新一条（departure_status / return / timeout） */
export function getDepartureSseByStreamKey (sseObj) {
  const m = sseObj?.data?.departureSseByStreamKey
  if (!m || typeof m !== 'object') return EMPTY_DEPARTURE_SSE_MAP
  return m
}

